import styled from 'styled-components';
import { getObjectWithSelector } from '../../../functions/objectFunctions/getObjectWithSelector';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { useScene } from '../../../hooks';
import { useElements } from '../../../hooks/useElements';
import { LauncherElement } from '../../../types/launcherElement';
import {
  fontFamilyOptions,
  fontWeightOptions,
  textDecorationLineOptions,
} from '../../../types/options';
import { sizeUnits } from '../../../types/units';
import { ColorInput } from '../inputs/ColorInput';
import { TextAlignInput } from '../inputs/TextAlignInput';
import { UnitInput } from '../inputs/UnitInput';
import { SelectInput } from '../inputs/selectInput/SelectInput';

export const TextInputGroup = ({
  variant,
  tab,
  pseudoclass,

  colorPickerExtended,
  setColorPickerExtended,
}: {
  variant?: string;
  tab?: string;
  pseudoclass?: string;

  colorPickerExtended?: boolean;
  setColorPickerExtended?: (newValue: boolean) => void;
}) => {
  const scene = useScene();
  const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
  const [selectedElement, setSelectedElement] = useElements<LauncherElement>({
    id: lastSelectedElementID,
  });

  let selector: string = 'props.class';
  if (variant && tab) {
    selector = `props.class.variants.${variant}.${tab}`;
  }

  if (pseudoclass === 'none') pseudoclass = undefined;
  if (pseudoclass) {
    selector = `${selector}.&:${pseudoclass}`;
  }

  const styleObj = getObjectWithSelector(selectedElement, selector);

  const labelWidth = 100;

  return (
    <Container>
      <TextAlignInput
        label="Text Align"
        value={styleObj?.textAlign}
        onChange={(newValue) =>
          setSelectedElement((prev) =>
            updateObjectNestedProperties(prev, {
              [`${selector}.textAlign`]: newValue,
            })
          )
        }
      />
      <UnitInput
        id={`${lastSelectedElementID}-${variant}-fontSize-${tab}-${pseudoclass}-input`}
        label="Font Size"
        value={styleObj?.fontSize}
        units={sizeUnits}
        defaultUnit="px"
        onChange={(inputValue) =>
          setSelectedElement((prev) =>
            updateObjectNestedProperties(prev, {
              [`${selector}.fontSize`]: inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <UnitInput
        id={`${lastSelectedElementID}-${variant}-lineHeight-${tab}-${pseudoclass}-input`}
        label="Line Height"
        value={styleObj?.lineHeight}
        units={sizeUnits}
        defaultUnit="px"
        onChange={(inputValue) =>
          setSelectedElement((prev) =>
            updateObjectNestedProperties(prev, {
              [`${selector}.lineHeight`]: inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <ColorInput
        id={`${lastSelectedElementID}-${variant}-color-${tab}-${pseudoclass}-input`}
        label="Font Color"
        value={styleObj?.color}
        onChange={(inputValue: string | undefined) =>
          setSelectedElement((prev) =>
            updateObjectNestedProperties(prev, {
              [`${selector}.color`]: inputValue,
            })
          )
        }
        labelWidth={labelWidth}
        extended={colorPickerExtended}
        setExtended={setColorPickerExtended}
      />
      <SelectInput
        options={fontFamilyOptions}
        id={`${lastSelectedElementID}-${variant}-fontFamily-${tab}-${pseudoclass}-input`}
        label="Font Family"
        value={styleObj?.fontFamily}
        onChange={(inputValue: string) =>
          setSelectedElement((prev) =>
            updateObjectNestedProperties(prev, {
              [`${selector}.fontFamily`]: inputValue,
            })
          )
        }
      />
      <SelectInput
        options={fontWeightOptions}
        id={`${lastSelectedElementID}-${variant}-fontWeight-${tab}-${pseudoclass}-input`}
        label="Font Weight"
        value={styleObj?.fontWeight}
        onChange={(inputValue: string) =>
          setSelectedElement((prev) =>
            updateObjectNestedProperties(prev, {
              [`${selector}.fontWeight`]: inputValue,
            })
          )
        }
      />
      <SelectInput
        options={textDecorationLineOptions}
        id={`${lastSelectedElementID}-${variant}-text-decoration-line-${tab}-${pseudoclass}-input`}
        label="Text Decoration Line"
        value={styleObj?.textDecorationLine}
        onChange={(inputValue: string) =>
          setSelectedElement((prev) =>
            updateObjectNestedProperties(prev, {
              [`${selector}.textDecorationLine`]: inputValue,
            })
          )
        }
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
